import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

interface PrivateRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, requiredRole }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const location = useLocation();
  const { isAuthenticated, currentUser } = useAuth();

  useEffect(() => {
    const checkAuth = () => {
      if (!isAuthenticated) {
        setIsAuthorized(false);
        setIsLoading(false);
        return;
      }
      if (requiredRole) {
        // super_admin hérite de tous les droits admin et manager
        if (requiredRole === 'admin') {
          setIsAuthorized(['admin', 'super_admin', 'manager'].includes(currentUser?.role ?? ''));
        } else if (requiredRole === 'super_admin') {
          // Modules exclusifs : uniquement le super_admin
          setIsAuthorized(currentUser?.role === 'super_admin');
        } else {
          setIsAuthorized(currentUser?.role === requiredRole);
        }
      } else {
        setIsAuthorized(true);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [requiredRole, isAuthenticated, currentUser]);

  if (isLoading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthorized) {
    // Rediriger vers la page de connexion avec l'URL de redirection
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;
